// ============================================
// LIST TABLE - BOOK VECTORIZATION
// ============================================
import React, { Component, Fragment } from 'react'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from 'material-ui/Table'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import { withStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import { connect } from 'react-redux'
import { bookVectorizationActions } from '../../actions'
import TablePagination from '@material-ui/core/TablePagination'
import Typography from '@material-ui/core/Typography'
import { listTableStyle } from '../../styles'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import Visibility from '@material-ui/icons/Visibility'
import Chip from '@material-ui/core/Chip'
import ProcessingModal from './ProcessingModal'
import ListIcon from '@material-ui/icons/List'
import LibraryBooks from '@material-ui/icons/LibraryBooks'
import { STATUS_CONFIG, isProcessing } from '../../constants/vectorization'
class ListTable extends Component {
  state = {
    debounce: undefined,
    processingIsbn: null,
    processingBookName: null,
    processingMode: 'status',
  }

  _pollInterval = null

  // ========================================
  // LIFECYCLE - Recarrega quando muda filtros
  // ========================================
  componentDidUpdate(prevProps) {
    const { pagination, order, fetchBookVectorizations, term, loadingBookVectorizations, bookVectorizations } = this.props
    const { page, rowsPerPage: perPage } = pagination

    // Auto-refresh: ativa polling quando há livros em processamento
    const hasProcessing = (bookVectorizations || []).some((item) => {
      const status = (item.processamento || {}).status_atual || (item.status || {}).status || ''
      return isProcessing(status)
    })

    if (hasProcessing && !this._pollInterval) {
      this._pollInterval = setInterval(() => {
        const { pagination: p, order: o, term: t, loadingBookVectorizations: loading } = this.props
        if (!loading) {
          fetchBookVectorizations({ page: p.page, perPage: p.rowsPerPage, order: o, term: t })
        }
      }, 30000)
    } else if (!hasProcessing && this._pollInterval) {
      clearInterval(this._pollInterval)
      this._pollInterval = null
    }

    if (loadingBookVectorizations) return

    const oldPagination = prevProps.pagination || {}
    const oldOrder = prevProps.order || {}
    const oldTerm = prevProps.term || ''

    const shouldReload =
      pagination.page !== oldPagination.page ||
      pagination.rowsPerPage !== oldPagination.rowsPerPage ||
      ((order || {}).column || '') !== (oldOrder.column || '') ||
      ((order || {}).direction || 1) !== (oldOrder.direction || 1) ||
      term !== oldTerm

    if (shouldReload) {
      const debounce = setTimeout(() => {
        fetchBookVectorizations({ page, perPage, order, term })
      }, 700)

      this.setState((state) => {
        clearTimeout(state.debounce)
        return { debounce }
      })
    }
  }

  // ========================================
  // LIFECYCLE - Carrega dados ao montar
  // ========================================
  componentDidMount() {
    const { fetchBookVectorizations } = this.props
    fetchBookVectorizations()
  }

  componentWillUnmount() {
    clearInterval(this._pollInterval)
    this._pollInterval = null
  }

  // ========================================
  // RENDER - Chip de status
  // ========================================
  renderStatus = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: 'default' }
    return <Chip label={config.label} color={config.color} size="small" />
  }

  renderFileSize = (bytes) => {
    if (!bytes) return '--'

    const mb = bytes / (1024 * 1024)
    if (mb >= 1) return mb.toFixed(2) + ' MB'

    return Math.round(bytes / 1024) + ' KB'
  }

  renderEllipsis = (value, title) => {
    return (
      <div
        title={title || value}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value || '--'}
      </div>
    )
  }

  // ========================================
  // RENDER - Botões de ação
  // ========================================
  renderActions = (item) => {
    const { fetchBookVectorization } = this.props

    return (
      <Fragment>
        {/* ====================================== */}
        {/* BOTÃO: Visualizar */}
        {/* ====================================== */}
        <IconButton
          onClick={() => {
            fetchBookVectorization(item.isbn || item.id)
          }}
          aria-label="Visualizar"
          title="Visualizar"
        >
          <Visibility nativeColor={'rgba(0, 0, 0, 0.87)'} />
        </IconButton>

        {item.isbn && (
          <IconButton
            onClick={() => {
              this.setState({
                processingIsbn: item.isbn,
                processingBookName: item.book_name,
                processingMode: 'status',
              })
            }}
            aria-label="Processamento"
            title="Ver status do processamento"
          >
            <ListIcon />
          </IconButton>
        )}

        {item.isbn && (
          <IconButton
            onClick={() => {
              this.setState({
                processingIsbn: item.isbn,
                processingBookName: item.book_name,
                processingMode: 'chapters',
              })
            }}
            aria-label="Capítulos"
            title="Revisar capítulos no CMS"
          >
            <LibraryBooks />
          </IconButton>
        )}

        {/* Botão de deletar removido — API não expõe exclusão de livros (501) */}
      </Fragment>
    )
  }

  // ========================================
  // HANDLERS - Paginação
  // ========================================
  handleChangePage = (event, page) => {
    const { handleChangePage } = this.props
    handleChangePage(page)
  }

  handleChangeRowsPerPage = (event) => {
    const perPage = event.target.value
    const { handleChangePerPage } = this.props
    handleChangePerPage(perPage)
  }

  // ========================================
  // HANDLERS - Ordenação
  // ========================================
  handleSortTable = (column) => {
    const { order, changeOrderColumn, changeOrderDirection } = this.props

    // Se clicar na mesma coluna, inverte a direção
    if (column === ((order || {}).column || '')) {
      const direction = ((order || {}).direction || 1) === -1 ? 1 : -1
      changeOrderDirection(direction)
    } else {
      // Se mudar de coluna, reinicia com ordem crescente
      changeOrderColumn(column)
    }
  }

  // ========================================
  // RENDER PRINCIPAL
  // ========================================
  render() {
    const {
      classes,
      bookVectorizations = [],
      loadingBookVectorizations,
      fetchBookVectorizationsError,
      pagination,
      order = {},
    } = this.props

    return (
      <Paper className={classes.wrapperTable + ' VectorizationTableWrapper'} elevation={4}>
        {/* ====================================== */}
        {/* LOADER - Mostra enquanto carrega */}
        {/* ====================================== */}
        {!!loadingBookVectorizations && (
          <div className={classes.wrapperLoader}>
            <CircularProgress />
          </div>
        )}

        {/* ====================================== */}
        {/* TABELA */}
        {/* ====================================== */}
        <Table
          className="VectorizationResponsiveTable"
          fixedHeader={true}
          width={'1320px'}
          wrapperStyle={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'var(--crud-table-height)',
          }}
        >
          {/* ====================================== */}
          {/* HEADER DA TABELA */}
          {/* ====================================== */}
          <TableHeader
            displaySelectAll={false}
            className={classes.tableHeader}
            adjustForCheckbox={false}
          >
            <TableRow>
              {/* COLUNA: Nome do Livro */}
              <TableHeaderColumn>
                <TableSortLabel
                  active={((order || {}).column || '') === 'book_name'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('book_name')
                  }}
                >
                  Nome do Livro
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: ISBN */}
              <TableHeaderColumn style={{ width: '145px' }}>
                <TableSortLabel
                  active={((order || {}).column || '') === 'isbn'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('isbn')
                  }}
                >
                  ISBN
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: Autores */}
              <TableHeaderColumn>
                <TableSortLabel
                  active={((order || {}).column || '') === 'autores'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('autores')
                  }}
                >
                  Autores
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: Especialidade */}
              <TableHeaderColumn style={{ width: '135px' }}>
                <TableSortLabel
                  active={((order || {}).column || '') === 'especialidade'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('especialidade')
                  }}
                >
                  Especialidade
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: Ano */}
              <TableHeaderColumn style={{ width: '80px' }}>
                <TableSortLabel
                  active={((order || {}).column || '') === 'ano'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('ano')
                  }}
                >
                  Ano
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: Status */}
              <TableHeaderColumn style={{ width: '135px' }}>
                <TableSortLabel
                  active={((order || {}).column || '') === 'status'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('status')
                  }}
                >
                  Status
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: Tamanho */}
              <TableHeaderColumn style={{ width: '105px' }}>
                <TableSortLabel
                  active={((order || {}).column || '') === 'tamanho_bytes'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('tamanho_bytes')
                  }}
                >
                  Tamanho
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: Data */}
              <TableHeaderColumn style={{ width: '160px' }}>
                <TableSortLabel
                  active={((order || {}).column || '') === 'created_at'}
                  direction={((order || {}).direction || 1) === 1 ? 'asc' : 'desc'}
                  onClick={() => {
                    this.handleSortTable('created_at')
                  }}
                >
                  Data
                </TableSortLabel>
              </TableHeaderColumn>

              {/* COLUNA: Ações */}
              <TableHeaderColumn style={{ width: '160px' }}>Ações</TableHeaderColumn>
            </TableRow>
          </TableHeader>

          {/* ====================================== */}
          {/* BODY DA TABELA */}
          {/* ====================================== */}
          <TableBody displayRowCheckbox={false} className={classes.tableBody}>
            {(bookVectorizations || []).length ? (
              // ========================================
              // TEM DADOS - Renderiza linhas
              // ========================================
              (bookVectorizations || []).map((item) => {
                const autoresTexto =
                  item.autores && item.autores.length > 0 ? item.autores.join(', ') : '--'
                const tamanhoBytes =
                  item.tamanho_bytes || ((item.arquivo || {}).tamanho_bytes || 0)

                return (
                  <TableRow key={item.id}>
                    {/* Nome do Livro */}
                    <TableRowColumn data-label="Nome do Livro">
                      {this.renderEllipsis(item.book_name, item.book_name)}
                    </TableRowColumn>

                    {/* ISBN */}
                    <TableRowColumn data-label="ISBN" style={{ width: '145px' }}>
                      {item.isbn || '--'}
                    </TableRowColumn>

                    {/* Autores */}
                    <TableRowColumn data-label="Autores">
                      {this.renderEllipsis(autoresTexto, autoresTexto)}
                    </TableRowColumn>

                    {/* Especialidade */}
                    <TableRowColumn data-label="Especialidade" style={{ width: '135px' }}>
                      {this.renderEllipsis(item.especialidade, item.especialidade)}
                    </TableRowColumn>

                    {/* Ano */}
                    <TableRowColumn data-label="Ano" style={{ width: '80px' }}>
                      {item.ano || '--'}
                    </TableRowColumn>

                    {/* Status */}
                    <TableRowColumn data-label="Status" style={{ width: '135px' }}>
                      {this.renderStatus((item.processamento || {}).status_atual || 'pendente')}
                    </TableRowColumn>

                    {/* Tamanho */}
                    <TableRowColumn data-label="Tamanho" style={{ width: '105px' }}>
                      {this.renderFileSize(tamanhoBytes)}
                    </TableRowColumn>

                    {/* Data */}
                    <TableRowColumn data-label="Data" style={{ width: '160px' }}>
                      {item.created_at ? new Date(item.created_at).toLocaleString('pt-BR') : '--'}
                    </TableRowColumn>

                    {/* Ações */}
                    <TableRowColumn
                      data-label="Ações"
                      className="VectorizationResponsiveActions"
                      style={{ width: '160px' }}
                    >
                      {this.renderActions(item)}
                    </TableRowColumn>
                  </TableRow>
                )
              })
            ) : (
              // ========================================
              // SEM DADOS - Mostra mensagem
              // ========================================
              <TableRow>
                <TableRowColumn>
                  <Typography noWrap style={{ textAlign: 'center', padding: '16px' }}>
                    {!!loadingBookVectorizations
                      ? 'Carregando...'
                      : fetchBookVectorizationsError
                        ? 'Erro ao carregar livros: ' + fetchBookVectorizationsError
                        : 'Não há registros.'}
                  </Typography>
                </TableRowColumn>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* ====================================== */}
        {/* PAGINAÇÃO */}
        {/* ====================================== */}
        <div className={classes.paginationWrapper}>
          <div className={classes.page}>Página: {pagination.page + 1}</div>
          <TablePagination
            component="div"
            className={classes.pagination}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count, page }) => {
              return 'Exibindo de ' + from + ' a ' + to + ' de ' + count
            }}
            count={pagination.length}
            rowsPerPage={pagination.rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            page={pagination.page}
            backIconButtonProps={{
              'aria-label': 'Página anterior',
            }}
            nextIconButtonProps={{
              'aria-label': 'Próxima página',
            }}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
          />
        </div>

        {this.state.processingIsbn && (
          <ProcessingModal
            open={!!this.state.processingIsbn}
            isbn={this.state.processingIsbn}
            bookName={this.state.processingBookName}
            mode={this.state.processingMode}
            onClose={() =>
              this.setState({
                processingIsbn: null,
                processingBookName: null,
                processingMode: 'status',
              })
            }
          />
        )}
      </Paper>
    )
  }
}

// ============================================
// REDUX - Conecta ESTADO ao componente
// ============================================
const mapStateToProps = (state) => {
  return {
    bookVectorization: state.bookVectorization.bookVectorization,
    bookVectorizations: state.bookVectorization.bookVectorizations,
    loadingBookVectorizations: state.bookVectorization.loadingBookVectorizations,
    fetchBookVectorizationsError: state.bookVectorization.fetchBookVectorizationsError,
    pagination: state.bookVectorization.bookVectorizationsPagination,
    order: state.bookVectorization.order,
    term: state.searchTable.term,
  }
}

// ============================================
// REDUX - Conecta ACTIONS ao componente
// ============================================
const mapDispatchToProps = (dispatch) => {
  return {
    // Busca um livro específico (abre modal)
    fetchBookVectorization: (id) => {
      dispatch(bookVectorizationActions.fetchBookVectorization(id))
    },

    // Busca lista de livros
    fetchBookVectorizations: (options) => {
      dispatch(bookVectorizationActions.fetchBookVectorizations(options))
    },

    // Muda página
    handleChangePage: (page) => {
      dispatch(bookVectorizationActions.handleChangePage(page))
    },

    // Muda itens por página
    handleChangePerPage: (perPage) => {
      dispatch(bookVectorizationActions.handleChangePerPage(perPage))
    },

    // Muda coluna de ordenação
    changeOrderColumn: (column) => {
      dispatch(bookVectorizationActions.changeOrderColumn(column))
    },

    // Muda direção de ordenação
    changeOrderDirection: (direction) => {
      dispatch(bookVectorizationActions.changeOrderDirection(direction))
    },
  }
}

// ============================================
// EXPORTA componente conectado ao Redux e com estilos
// ============================================
export default connect(mapStateToProps, mapDispatchToProps)(withStyles(listTableStyle)(ListTable))



// WEBPACK FOOTER //
// ./src/containers/BookVectorization/ListTable.js